import axios from "axios";
import { Messages } from "../constants/Messages";
import { Template } from "../types/Template";
import { setupAPIRequestInterceptor, setupAPIResponseInterceptor } from "../utils/Authorization";

// Base URL configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.materiallogs.barkbyborders.com";

// Interfaces for request and response payloads
export interface Material {
    template_id?: number;
    material_type_id: number | null;
    material_size_id: number | null;
    quantity: any;
    quantity_unit?: string;
}

interface CreateTemplatePayload {
    name: string;
    materials: Material[];
    id?: number;
}

export interface UpdateTemplatePayload extends CreateTemplatePayload {
}

export interface TemplateDetailsResponse {
    name: string;
    materials: Material[];
    id?: number;
}

// Axios instance for template API
const templateAPI = axios.create({
    baseURL: `${API_BASE_URL}/templates`,
});

setupAPIRequestInterceptor(templateAPI);
setupAPIResponseInterceptor(templateAPI);

/* =======================
 Template API Functions
 ======================= */

/**
 * Create a new product template
 */
export const createTemplate = async (payload: CreateTemplatePayload): Promise<any> => {
    try {
        const response = await templateAPI.post("/", payload);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_CREATE_TEMPLATE);
    }
};

/**
 * Update an existing product template
 */
export const updateTemplate = async (id: string | number, payload: UpdateTemplatePayload): Promise<any> => {
    try {
        const response = await templateAPI.put(`/${id}`, payload);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_TEMPLATE);
    }
};

/**
 * Fetch all templates
 */
export const fetchTemplates = async (): Promise<Template[]> => {
    try {
        const response = await templateAPI.get("/");
        const data = response.data.productTemplates;

        if (!Array.isArray(data)) {
            throw new Error(Messages.ERROR_TEMPLATES_ARRAY);
        }

        return data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH_TEMPLATES);
    }
};

/**
 * Fetch details of a specific template
 */
export const fetchTemplateDetails = async (id: string | number): Promise<TemplateDetailsResponse> => {
    try {
        const response = await templateAPI.get(`/${id}`);
        return response.data.productTemplate;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH_TEMPLATE);
    }
};

/**
 * Delete a specific template
 */
export const deleteTemplate = async (id: string | number): Promise<void> => {
    try {
        await templateAPI.delete(`/${id}`);
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_DELETE_TEMPLATE);
    }
};

export default templateAPI;
